import { observer } from 'mobx-react-lite'
import { Forbidden } from 'fejl'
import { useRevalidator } from 'react-router'
import { useFeatures } from '@/contexts/root-store-context'
import { AccessDeniedView } from '@/components/AccessDeniedView'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { type DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { DeleteDraftJobDialogContainer } from '@/features/jobs/drafting/components/DeleteDraftJobDialog/DeleteDraftJobDialogContainer'
import { RenameJobDialogContainer } from '@/features/jobs/RenameJobDialog/RenameJobDialogContainer'
import { CopyJobDialogContainer } from '@/features/jobs/CopyJobDialog/CopyJobDialogContainer'
import { JobCreation } from '@/features/jobs/drafting/components/JobCreation'
import { HeaderContainer } from '@/features/jobs/drafting/components/JobCreation/Header/HeaderContainer'
import { JobCreationFormViewContainer } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/JobCreationFormViewContainer'
import { OutlineContainer } from '@/features/jobs/drafting/components/JobCreation/Outline/OutlineContainer'

export default observer(function JobCreationContainer() {
  const {
    internal: { jobCreationRootVm: vm },
  } = useFeatures(({ jobs }) => ({
    internal: jobs.internal(),
  }))
  const revalidator = useRevalidator()

  useDocumentTitle('Loading...')
  useActivation(vm)

  return vm.edit.match({
    pending: () => <ContentLoading />,
    rejected: (err) =>
      err instanceof Forbidden ? (
        <Denied />
      ) : (
        <ContentError size="lg" error={err} onRetry={revalidator.revalidate} />
      ),
    resolved: (editor) =>
      vm.canViewEditor ? (
        <JobCreationEditorContainer vm={editor} />
      ) : (
        <Denied />
      ),
  })
})

interface JobCreationEditorContainerProps {
  vm: DraftJobViewModel
}

const JobCreationEditorContainer = observer(
  function JobCreationEditorContainer({ vm }: JobCreationEditorContainerProps) {
    useDocumentTitle(vm.viewTitle)
    return (
      <JobCreation
        outline={<OutlineContainer title={vm.viewTitle} vm={vm.outline} />}
        header={<HeaderContainer vm={vm} />}
        dialogs={
          <>
            <DeleteDraftJobDialogContainer vm={vm.deleteDialogVm} />
            <RenameJobDialogContainer vm={vm.renameModal} />
            <CopyJobDialogContainer vm={vm.copyModal} />
          </>
        }
      >
        <JobCreationFormViewContainer vm={vm} />
      </JobCreation>
    )
  },
)

const Denied = observer(function Denied() {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  useDocumentTitle('Access Denied')
  return <AccessDeniedView corporateMode={shellInfo.corporateMode} />
})
